import { InjectionToken } from '@angular/core';
import { RoleType } from '@/core/admin/constants/role.constants';
import { AppSection } from './app-registry';

/**
 * Admin registry: the single source of truth for the Administration area
 * (`/admin/*`), shaped like {@link APP_REGISTRY} in `app-registry.ts`
 * (SCRUM-619, admin masthead epic story 1).
 *
 * An admin **group** is to the admin masthead what an app is to the app
 * masthead: one entry per group in sidebar order, each with its **pages**
 * (the masthead tabs; `pages[0]` is the default). The admin routes in
 * `core/admin/admin.routes.ts` carry `data.adminGroup: AdminGroupKey` so the
 * layout can look a group up by route.
 *
 * Gates were carried over from the page-by-page sidebar this registry
 * replaced, so nobody gained or lost a page; the admin sidebar is now derived
 * from this registry (`buildAdminMenuModel` in `admin-menu.model.ts`,
 * SCRUM-622) and `admin-registry.spec.ts` proves the parity per role. A page
 * carries `requiredRoles` only where it is stricter than its group. Links are
 * absolute admin URLs and must resolve to real routes (checked by the spec too).
 *
 * Gating has the same meaning as on the sidebar items and the app registry:
 * see `isMenuItemVisible` in `menu-visibility.ts`. Active-page resolution is
 * `resolveActiveSection` in `active-section.ts`: a page's `activeWhen`
 * defaults to `[link]`, and a prefix matches the whole path or the path
 * followed by `/`, so a list page's link already covers its create and
 * entity URLs (`/admin/users` covers `/admin/users/add` and
 * `/admin/users/7/edit`).
 *
 * Everything here is plain data (the sidebar model is deep-cloned through
 * JSON), and `icon` is the PrimeIcons class of the group's sidebar row
 * (admin rows use PrimeIcons, not glyphs; the masthead tile is always the gear).
 */

export type AdminGroupKey =
  | 'users'
  | 'teams'
  | 'devices'
  | 'workspace-management'
  | 'priority-score'
  | 'strategic-imperatives'
  | 'categories'
  | 'opportunity-settings';

/** A page of an admin group: the same shape as an app section (key, label, link, activeWhen, requiredRoles). */
export type AdminPage = AppSection;

export interface AdminGroupDefinition {
  key: AdminGroupKey;
  label: string;
  /** PrimeIcons class of the group's sidebar row (e.g. `pi pi-fw pi-users`). */
  icon: string;
  /** Sidebar target. */
  home: string;
  /** URL prefixes that belong to the group. */
  activeWhen: readonly string[];
  /** `pages[0]` is the default page. */
  pages: readonly AdminPage[];
  /** Roles required to see the group (ANY of these unless `requireAllRoles`). */
  requiredRoles?: readonly string[];
  requireAllRoles?: boolean;
  /** Data gate: needs a WM-enabled team (System/Org admins exempt). */
  requiresWmEnabledTeam?: boolean;
}

/** Shape of `data` on the admin routes in `core/admin/admin.routes.ts` (mirror of `AppRouteData`). */
export interface AdminRouteData {
  breadcrumb: string;
  adminGroup: AdminGroupKey;
}

/**
 * What the Administration area is called everywhere the user can read it:
 * the masthead eyebrow, the root breadcrumb and the document title (epic
 * SCRUM-618 decision). "Admin" stays in URLs and code identifiers only.
 */
export const ADMIN_AREA_LABEL = 'Administration';

const ADMIN_PREFIX = '/admin';
const WM_PREFIX = `${ADMIN_PREFIX}/workspace-management`;

const SA = RoleType.SYSTEM_ADMIN;
const OA = RoleType.ORGANIZATION_ADMIN;
const TL = RoleType.TEAM_LEAD;

/** Sidebar order and masthead order: `buildAdminMenuModel` lists the groups as declared here. */
export const ADMIN_REGISTRY: readonly AdminGroupDefinition[] = [
  {
    key: 'users',
    label: 'Users',
    icon: 'pi pi-fw pi-users',
    home: `${ADMIN_PREFIX}/users`,
    activeWhen: [`${ADMIN_PREFIX}/users`],
    requiredRoles: [SA],
    // `/admin/users/add` and `/admin/users/:id/edit` light this page (prefix of the link).
    pages: [{ key: 'users', label: 'Users', link: `${ADMIN_PREFIX}/users` }],
  },
  {
    key: 'teams',
    label: 'Teams',
    icon: 'pi pi-fw pi-folder',
    home: `${ADMIN_PREFIX}/teams`,
    // Finance thresholds are a team finance setting; the page is reachable by URL
    // only (no tab, no link to it anywhere), so it belongs to the group but has no page.
    activeWhen: [
      `${ADMIN_PREFIX}/teams`,
      `${ADMIN_PREFIX}/team-types`,
      `${ADMIN_PREFIX}/shifts`,
      `${ADMIN_PREFIX}/finance-thresholds`,
    ],
    requiredRoles: [OA, TL],
    pages: [
      // `/admin/teams/add` and `/admin/teams/:id` light this page (prefix of the link).
      { key: 'teams', label: 'Teams', link: `${ADMIN_PREFIX}/teams` },
      { key: 'team-types', label: 'Team Types', link: `${ADMIN_PREFIX}/team-types`, requiredRoles: [SA] },
      { key: 'shifts', label: 'Shifts', link: `${ADMIN_PREFIX}/shifts`, requiredRoles: [OA] },
    ],
  },
  {
    key: 'devices',
    label: 'Devices',
    icon: 'pi pi-fw pi-desktop',
    home: `${ADMIN_PREFIX}/devices`,
    activeWhen: [`${ADMIN_PREFIX}/devices`],
    requiredRoles: [OA],
    pages: [{ key: 'devices', label: 'Devices', link: `${ADMIN_PREFIX}/devices` }],
  },
  {
    // Workspace Management setup + the System-admin observability overview.
    // The whole group is data-gated: hidden unless the user has a WM-enabled
    // team (System/Org admins always see it). Areas & Zones is the entry page.
    key: 'workspace-management',
    label: 'Workspace Management',
    icon: 'pi pi-fw pi-clipboard',
    home: `${WM_PREFIX}/areas`,
    activeWhen: [WM_PREFIX],
    requiresWmEnabledTeam: true,
    requiredRoles: [SA, OA, TL],
    pages: [
      { key: 'areas', label: 'Areas & Zones', link: `${WM_PREFIX}/areas` },
      { key: 'checklists', label: 'Checklists', link: `${WM_PREFIX}/checklists` },
      { key: 's-config', label: 'S Configuration', link: `${WM_PREFIX}/s-config`, requiredRoles: [SA, OA] },
      {
        // `/admin/workspace-management/audit-definitions/:id` lights this page (prefix of the link).
        key: 'audit-definitions',
        label: 'Audit Configuration',
        link: `${WM_PREFIX}/audit-definitions`,
        requiredRoles: [SA, OA],
      },
      {
        key: 'overview',
        label: 'Overview',
        link: `${WM_PREFIX}/overview`,
        // The team detail pages of the overview live under /teams/:teamId.
        activeWhen: [`${WM_PREFIX}/overview`, `${WM_PREFIX}/teams`],
        requiredRoles: [SA],
      },
    ],
  },
  {
    key: 'priority-score',
    label: 'Priority Score',
    icon: 'pi pi-fw pi-folder',
    home: `${ADMIN_PREFIX}/priority-score`,
    activeWhen: [`${ADMIN_PREFIX}/priority-score`],
    requiredRoles: [OA],
    // `/admin/priority-score/add` and `/admin/priority-score/:id` light this page (prefix of the link).
    pages: [{ key: 'forms', label: 'Forms', link: `${ADMIN_PREFIX}/priority-score` }],
  },
  {
    key: 'strategic-imperatives',
    label: 'Strategic Imperatives',
    icon: 'pi pi-fw pi-star',
    home: `${ADMIN_PREFIX}/strategic-imperatives`,
    activeWhen: [`${ADMIN_PREFIX}/strategic-imperatives`],
    requiredRoles: [OA],
    pages: [
      { key: 'strategic-imperatives', label: 'Strategic Imperatives', link: `${ADMIN_PREFIX}/strategic-imperatives` },
    ],
  },
  {
    key: 'categories',
    label: 'Categories',
    icon: 'pi pi-fw pi-tags',
    home: `${ADMIN_PREFIX}/categories/in-review-reason`,
    activeWhen: [`${ADMIN_PREFIX}/categories`],
    requiredRoles: [OA],
    pages: [
      { key: 'in-review-reason', label: 'In Review Reasons', link: `${ADMIN_PREFIX}/categories/in-review-reason` },
      { key: 'backlog-reason', label: 'Backlog Reasons', link: `${ADMIN_PREFIX}/categories/backlog-reason` },
      { key: 'decline-reason', label: 'Decline Reasons', link: `${ADMIN_PREFIX}/categories/decline-reason` },
    ],
  },
  {
    key: 'opportunity-settings',
    label: 'Opportunity Settings',
    icon: 'pi pi-fw pi-check-square',
    home: `${ADMIN_PREFIX}/opportunities`,
    activeWhen: [`${ADMIN_PREFIX}/opportunities`, `${ADMIN_PREFIX}/tasks`],
    requiredRoles: [OA],
    pages: [
      { key: 'opportunities', label: 'All Opportunities', link: `${ADMIN_PREFIX}/opportunities` },
      { key: 'tasks', label: 'Tasks', link: `${ADMIN_PREFIX}/tasks` },
    ],
  },
];

/**
 * Injectable view of {@link ADMIN_REGISTRY} so the admin masthead container
 * can be fed a stub registry in specs. Production code never overrides it.
 */
export const ADMIN_REGISTRY_TOKEN = new InjectionToken<readonly AdminGroupDefinition[]>('ADMIN_REGISTRY', {
  providedIn: 'root',
  factory: () => ADMIN_REGISTRY,
});

export function findAdminGroup(
  key: AdminGroupKey,
  registry: readonly AdminGroupDefinition[] = ADMIN_REGISTRY,
): AdminGroupDefinition {
  const group = registry.find((g) => g.key === key);
  if (!group) {
    throw new Error(`Unknown admin group key: ${key}`);
  }
  return group;
}
